import { lazy, Suspense } from 'react'
import MaintenanceBanner from './components/MaintenanceBanner'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop'
import ImpersonationBanner from './components/ImpersonationBanner'
import { useAuth } from './hooks/useAuth'
import LoadingSpinner from './components/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'

// PublicLayout + LandingPageContrast stay eager — the landing page is
// the LCP target so we don't want to wait on a chunk fetch.
// Layout (dashboard chrome) is lazy — only paid users hit /dashboard,
// no point shipping it on every landing render.
import PublicLayout from './components/PublicLayout'
import LandingPageContrast from './pages/LandingPageContrast'
const Layout = lazy(() => import('./components/Layout'))

// Lazy pages — each becomes its own JS chunk that Vite emits separately.
// Visitors only download the chunks for the routes they visit.
const Login = lazy(() => import('./pages/Login'))
// Dashboard is imported by DashboardRouter (it renders Dashboard for
// non-recruiter mode). The route itself uses DashboardRouter so the
// lazy chunk here is the router; Dashboard rides along.
const DashboardRouter = lazy(() => import('./pages/DashboardRouter'))
const NewAnalysis = lazy(() => import('./pages/NewAnalysis'))
const AnalysisDetail = lazy(() => import('./pages/AnalysisDetail'))
const RecruiterWorkspace = lazy(() => import('./pages/RecruiterWorkspace'))
const WorkspaceHome = lazy(() => import('./pages/RecruiterWorkspace').then((m) => ({ default: m.WorkspaceHome })))
const RecruiterNewBatch = lazy(() => import('./pages/RecruiterNewBatch'))
const RecruiterBatchResults = lazy(() => import('./pages/RecruiterBatchResults'))
const RecruiterCandidateView = lazy(() => import('./pages/RecruiterCandidateView'))
const RecruiterCompareView = lazy(() => import('./pages/RecruiterCompareView'))
const RecruiterApplicants = lazy(() => import('./pages/RecruiterApplicants'))
const RecruiterPool = lazy(() => import('./pages/RecruiterPool'))
const HomePage = lazy(() => import('./pages/HomePage'))
const AnalyzePage = lazy(() => import('./pages/AnalyzePage'))
const TryResult = lazy(() => import('./pages/TryResult'))
const TryResultV2 = lazy(() => import('./pages/TryResultV2'))

// Keyed wrapper for TryResultV2 — forces a full unmount/remount when
// jobId changes. Without this, React Router reuses the component
// across param changes and stale per-job state (tailorState, preview,
// jobActions, paidArtifacts) leaks from the previous job's session.
function TryResultV2Keyed() {
  const { jobId } = useParams<{ jobId: string }>()
  return <TryResultV2 key={jobId} />
}
const AnonTryResult = lazy(() => import('./pages/AnonTryResult'))
const BloatCheck = lazy(() => import('./pages/BloatCheck'))
const BsMeter = lazy(() => import('./pages/BsMeter'))
const RecruiterBulkBloat = lazy(() => import('./pages/RecruiterBulkBloat'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Behind = lazy(() => import('./pages/Behind'))
const MagicLink = lazy(() => import('./pages/MagicLink'))
const MagicLinkCheck = lazy(() => import('./pages/MagicLinkCheck'))
const MagicLinkCallback = lazy(() => import('./pages/MagicLinkCallback'))
const UnlockCallback = lazy(() => import('./pages/UnlockCallback'))
const Pricing = lazy(() => import('./pages/Pricing'))
const BillingSuccess = lazy(() => import('./pages/BillingSuccess'))
const BillingCancel = lazy(() => import('./pages/BillingCancel'))
const Settings = lazy(() => import('./pages/Settings'))
const AccountForensics = lazy(() => import('./pages/AccountForensics'))
const Insights = lazy(() => import('./pages/Insights'))
const Terms = lazy(() => import('./pages/Terms'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Contact = lazy(() => import('./pages/Contact'))
const RecruiterOnboarding = lazy(() => import('./pages/RecruiterOnboarding'))
const FitToHireLandingV2 = lazy(() => import('./pages/FitToHireLandingV2'))
const Checkout = lazy(() => import('./pages/Checkout'))
const Affiliates = lazy(() => import('./pages/Affiliates'))
const AffiliatePortal = lazy(() => import('./pages/AffiliatePortal'))
const Extension = lazy(() => import('./pages/Extension'))
const ExtensionInstall = lazy(() => import('./pages/ExtensionInstall'))
const LandingPage = lazy(() => import('./pages/LandingPage'))
const Recruiters = lazy(() => import('./pages/Recruiters'))
const RecruiterSandbox = lazy(() => import('./pages/RecruiterSandbox'))
const SeekerSandbox = lazy(() => import('./pages/SeekerSandbox'))
const LandSignUpPage = lazy(() => import('./pages/LandSignUpPage'))
const LandSignInPage = lazy(() => import('./pages/LandSignInPage'))
const LandFAQPage = lazy(() => import('./pages/LandFAQPage'))
const LandEvaluationPage = lazy(() => import('./pages/LandEvaluationPage'))


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}


// The marketing landing at "/" is for logged-OUT visitors only. A logged-in
// user goes straight to their workspace — DashboardRouter then routes recruiters
// to the recruiter dashboard and job-seekers to theirs. Never show a logged-in
// recruiter the candidate marketing page.
function RootRoute() {
  const { isAuthenticated, loading } = useAuth()
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    )
  }
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  return <LandingPageContrast />
}


// Suspense fallback shown while a route's chunk is being fetched. Same
// look as the auth loading state so transitions feel consistent.
function RouteSuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" message="Loading..." />
    </div>
  )
}


function App() {
  return (
    <>
    <ImpersonationBanner />
    {/* Outside <Routes> so it survives navigation — the decision is
        site-wide, not per-page. Renders nothing once a choice is stored. */}
    <ScrollToTop />
    {/* Outside <Routes> so it shows on EVERY page, including ones a visitor
        is already sitting on when the window starts. */}
    <MaintenanceBanner />
    <Suspense fallback={<RouteSuspenseFallback />}>
    <Routes>
      {/* Landing page — contrast version (v9b). Eager-loaded since it's
          the LCP target. */}
      <Route path="/" element={<ErrorBoundary><RootRoute /></ErrorBoundary>} />
      {/* Stable, always-viewable candidate landing — the candidate logo target
          (since "/" now redirects a logged-in user to their dashboard). */}
      <Route path="/home" element={<ErrorBoundary><LandingPageContrast /></ErrorBoundary>} />
      <Route path="/recruiters" element={<ErrorBoundary><Recruiters /></ErrorBoundary>} />
      {/* The demo owns a route SUBTREE. Reusing the dashboard's links sent an
          anonymous visitor to a login screen from the rail, from Compare and
          from the full report — every one a dead end. Everything reachable
          from the demo now lives under it. */}
      {/* The candidate demo: intake replay at the base, the real report
          (candidate voice) under it. Same sandbox client as the recruiter demo. */}
      <Route path="/demo/report" element={<ErrorBoundary><SeekerSandbox /></ErrorBoundary>}>
        <Route path=":jobId" element={<TryResultV2Keyed />} />
      </Route>
      <Route path="/recruiters/demo" element={<ErrorBoundary><RecruiterSandbox /></ErrorBoundary>}>
        <Route path="compare" element={<RecruiterCompareView />} />
        <Route path="report/:jobId" element={<TryResultV2Keyed />} />
      </Route>

      {/* Auth pages — minimal chrome (industry standard) */}
      <Route path="/login" element={<ErrorBoundary><LandSignInPage /></ErrorBoundary>} />
      <Route path="/signin" element={<ErrorBoundary><LandSignInPage /></ErrorBoundary>} />
      {/* Legacy signup surfaces — redirect to the real (passwordless) login.
          /signup/recruiter preserves recruiter intent via ?persona=. */}
      {/* A real sign-UP destination. It used to bounce to /login, which told
          people with no account to "sign in" — the only exit the demo had. */}
      {/* A real sign-UP page, RENDERED — not a redirect into /login.
          Bouncing to /login meant the demo's "New position" CTA landed a
          visitor on a page whose URL and heading both said "sign in", for an
          account they have never had. The route now keeps its own URL so the
          funnel is legible in analytics and to the person clicking it. */}
      <Route path="/signup/recruiter" element={<ErrorBoundary><LandSignUpPage persona="recruiter" /></ErrorBoundary>} />
      <Route path="/signup" element={<ErrorBoundary><LandSignUpPage /></ErrorBoundary>} />
      <Route path="/signup/applicant" element={<Navigate to="/login" replace />} />
      <Route path="/magic-link" element={<MagicLink />} />
      <Route path="/magic-link/check" element={<MagicLinkCheck />} />
      <Route path="/magic-link/callback" element={<MagicLinkCallback />} />
      <Route path="/unlock" element={<UnlockCallback />} />

      {/* Public pages — shared TopNav + Footer via PublicLayout */}
      <Route element={<ErrorBoundary><PublicLayout /></ErrorBoundary>}>
        <Route path="/evaluation" element={<LandEvaluationPage />} />
        <Route path="/faq" element={<LandFAQPage />} />
        <Route path="/behind" element={<Behind />} />
        <Route path="/extension" element={<Extension />} />
        <Route path="/extension/install" element={<ExtensionInstall />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/resume-bloat-check" element={<BloatCheck />} />
        <Route path="/resume-checker" element={<BloatCheck />} />
        <Route path="/bs-meter" element={<BsMeter />} />
        <Route path="/resume-bs-meter" element={<BsMeter />} />
        <Route path="/recruiters/bulk-bloat" element={<RecruiterBulkBloat />} />
        <Route path="/affiliates" element={<Affiliates />} />
        <Route path="/partners" element={<Affiliates />} />
        <Route path="/affiliate" element={<AffiliatePortal />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/* Transactional flows — TopNav via PublicLayout */}
      <Route element={<ErrorBoundary><PublicLayout /></ErrorBoundary>}>
        <Route path="/analyze" element={<AnalyzePage />} />
        <Route path="/try/:jobId" element={<TryResult />} />
        <Route path="/try-v2/:jobId" element={<TryResultV2Keyed />} />
        <Route path="/result/:jobId" element={<TryResult />} />
        <Route path="/checkout/:jobId" element={<Checkout />} />
        <Route path="/anon/:jobId" element={<AnonTryResult />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
      </Route>

      {/* Protected routes — Dashboard area */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <Layout />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardRouter />} />
        <Route path="new" element={<NewAnalysis />} />
        <Route path="analyze" element={<AnalyzePage />} />
        <Route path="analysis/:jobId" element={<AnalysisDetail />} />
        {/* Recruiter workspace — app shell: Positions rail + panel. The index
            and a selected position render inside the shell; new/compare/onboarding
            stay standalone (static paths outrank :batchId). */}
        <Route path="recruiter" element={<RecruiterWorkspace />}>
          <Route index element={<WorkspaceHome />} />
          <Route path="pool" element={<RecruiterPool />} />
          <Route path=":batchId" element={<RecruiterBatchResults />} />
        </Route>
        <Route path="recruiter/applicants" element={<RecruiterApplicants />} />
        <Route path="recruiter/new" element={<RecruiterNewBatch />} />
        <Route path="recruiter/:batchId/compare" element={<RecruiterCompareView />} />
        <Route path="recruiter/:batchId/:candidateId" element={<RecruiterCandidateView />} />
        <Route path="recruiter/onboarding" element={<RecruiterOnboarding />} />
        <Route path="settings" element={<Settings />} />
        <Route path="insights" element={<Insights />} />
        <Route path="forensics" element={<AccountForensics />} />
      </Route>

      {/* Archived pages */}
      <Route path="/old-landing" element={<ErrorBoundary><LandingPage /></ErrorBoundary>} />
      <Route path="/old-home" element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
      <Route path="/old-fittohire" element={<ErrorBoundary><FitToHireLandingV2 /></ErrorBoundary>} />
      <Route path="/old-login" element={<Login />} />

      {/* 404 */}
      <Route element={<PublicLayout />}>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </Suspense>
    </>
  )
}

export default App
